import axios from 'axios';
import { getAuthHeader } from './authService';

const API_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export interface FilteredSurveyResult {
  id: number;
  studentId: number;
  studentName: string;
  studentEmail: string;
  schoolId: string;
  assessmentId: number;
  assessmentTitle: string;
  category: string;
  score: number;
  maxScore: number;
  riskLevel: string;
  completedAt: string;
  teamName?: string;
  providerName?: string;
  flags?: string[];
}

export interface FilterCriteria {
  searchTerm?: string;
  assessmentId?: number | string;
  category?: string;
  riskLevel?: string;
  teamId?: number | string;
  dateFrom?: Date | null;
  dateTo?: Date | null;
  showOnlyFlagged?: boolean;
  page?: number;
  size?: number;
}

export const surveyResultService = {
  async getFilteredResults(filters: FilterCriteria): Promise<{ content: FilteredSurveyResult[], totalElements: number }> {
    try {
      const params: any = {
        page: filters.page || 0,
        size: filters.size || 20
      };

      // Map frontend filters to backend parameters
      if (filters.riskLevel) {
        params.flagLevel = filters.riskLevel;
      }
      if (filters.teamId) {
        params.teamId = filters.teamId;
      }
      if (filters.dateFrom) {
        params.fromDate = filters.dateFrom.toISOString();
      }
      if (filters.dateTo) {
        params.toDate = filters.dateTo.toISOString();
      }
      if (filters.assessmentId) {
        params.surveyIds = [filters.assessmentId];
      }
      if (filters.showOnlyFlagged) {
        params.consented = false; // Assuming this maps to flagged results
      }

      const response = await axios.get(`${API_URL}/provider/survey-results`, {
        headers: getAuthHeader(),
        params
      });

      // Transform the response to match frontend expectations
      const transformedResults = response.data.map((result: any) => ({
        id: result.id,
        studentId: result.patientId || result.studentId,
        studentName: result.patientName || result.studentName || 'Unknown',
        studentEmail: result.patientEmail || result.studentEmail || '',
        schoolId: result.schoolId || '',
        assessmentId: result.surveyId || result.assessmentId,
        assessmentTitle: result.surveyTitle || result.assessmentTitle || 'Unknown Assessment',
        category: result.category || 'General',
        score: result.score || 0,
        maxScore: result.maxScore || 100,
        riskLevel: result.flagLevel || result.riskLevel || 'LOW',
        completedAt: result.completedAt || result.createdAt,
        teamName: result.teamName,
        providerName: result.providerName,
        flags: result.flags || []
      }));

      return {
        content: transformedResults,
        totalElements: transformedResults.length
      };
    } catch (error: any) {
      console.error('Error fetching filtered results:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch filtered results');
    }
  },

  async exportResults(resultIds: number[], format: 'csv' | 'pdf'): Promise<Blob> {
    try {
      const endpoint = format === 'csv' 
        ? `${API_URL}/provider/survey-results/export`
        : `${API_URL}/provider/survey-results/export/pdf`; // PDF endpoint may not exist yet

      const response = await axios.get(endpoint, {
        headers: getAuthHeader(),
        params: {
          patientIds: resultIds
        },
        responseType: 'blob'
      });

      return response.data;
    } catch (error: any) {
      console.error('Error exporting results:', error);
      throw new Error(error.response?.data?.message || 'Failed to export results');
    }
  }
};